import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, MenuItem, IconButton, Button, Alert, CircularProgress,
  Chip, Tooltip
} from '@mui/material';
import { Add, Delete, Home, PostAdd, Save, ReceiptLong } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { useBankAccounts } from '../hooks/useBankAccounts';
import { useSuppliers } from '../hooks/useSuppliers';
import { glAccountOptions } from '../utils/glAccountOptions';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const today = () => new Date().toISOString().split('T')[0];

const ACCOUNT_TYPES  = ['G/L Account', 'Bank Account', 'Vendor', 'Customer', 'Fixed Asset', 'Employee'];
const DOCUMENT_TYPES = ['', 'Payment', 'Invoice', 'Credit Memo', 'Refund'];

const emptyLine = () => ({
  _key: Date.now() + Math.random(),
  postingDate: today(), documentType: '', documentNo: '',
  accountType: 'G/L Account', accountNo: '', description: '',
  debit: '', credit: '', balAccountType: 'G/L Account', balAccountNo: '', dimensions: {},
});

const GeneralJournal = () => {
  const navigate = useNavigate();
  const { accounts: glAccounts } = useGLAccounts();
  const { banks } = useBankAccounts();
  const { suppliers } = useSuppliers();

  const [learners, setLearners]         = useState([]);
  const [employees, setEmployees]       = useState([]);
  const [fixedAssets, setFixedAssets]   = useState([]);
  const [dimensions, setDimensions]     = useState([]);   // shortcut dims (first 2)
  const [dimValues, setDimValues]       = useState({});   // { CODE: [values] }

  const [templates, setTemplates]       = useState([]);
  const [batches, setBatches]           = useState([]);
  const [templateCode, setTemplateCode] = useState('');
  const [batchCode, setBatchCode]       = useState('');

  const [lines, setLines]     = useState([emptyLine()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Reference lists that aren't covered by a hook
  useEffect(() => {
    axios.get(`${API}/api/learners`, { headers: hdrs() }).then(r => setLearners(r.data || [])).catch(() => {});
    axios.get(`${API}/api/employees`, { headers: hdrs() }).then(r => setEmployees(r.data || [])).catch(() => {});
    axios.get(`${API}/api/fixed-assets`, { headers: hdrs() }).then(r => setFixedAssets(r.data || [])).catch(() => {});
    // Shortcut dimensions (first two) + their values
    axios.get(`${API}/api/finance/dimensions`, { headers: hdrs() }).then(async r => {
      const ds = (r.data || []).slice(0, 2);
      setDimensions(ds);
      const map = {};
      await Promise.all(ds.map(async d => {
        try { const v = await axios.get(`${API}/api/finance/dimension-values`, { headers: hdrs(), params: { dimension: d.code } }); map[d.code] = v.data || []; }
        catch { map[d.code] = []; }
      }));
      setDimValues(map);
    }).catch(() => {});
  }, []);

  // Templates (auto-seeds GENERAL/DEFAULT) → batches → lines
  useEffect(() => {
    axios.get(`${API}/api/finance/journal-templates`, { headers: hdrs() })
      .then(r => {
        setTemplates(r.data || []);
        if (r.data?.length) setTemplateCode(r.data[0].code);
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load templates'));
  }, []);

  useEffect(() => {
    if (!templateCode) return;
    axios.get(`${API}/api/finance/journal-batches`, { headers: hdrs(), params: { template: templateCode } })
      .then(r => {
        setBatches(r.data || []);
        setBatchCode(r.data?.[0]?.code || '');
      })
      .catch(() => setBatches([]));
  }, [templateCode]);

  const loadLines = useCallback(async () => {
    if (!templateCode || !batchCode) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/finance/journals/${templateCode}/${batchCode}/lines`, { headers: hdrs() });
      setLines(data.length ? data.map(l => ({
        ...l, _key: Math.random(),
        postingDate: l.postingDate ? l.postingDate.split('T')[0] : today(),
        debit: l.debit || '', credit: l.credit || '',
      })) : [emptyLine()]);
    } catch { setLines([emptyLine()]); }
    finally { setLoading(false); }
  }, [templateCode, batchCode]);

  useEffect(() => { loadLines(); }, [loadLines]);

  // ── Persist lines (debounced autosave) ──
  const savePayload = useRef();
  savePayload.current = { templateCode, batchCode, lines };
  const persist = useCallback(async () => {
    const { templateCode: t, batchCode: b, lines: ls } = savePayload.current;
    if (!t || !b) return;
    const clean = ls
      .filter(l => l.accountNo || l.description || l.debit || l.credit)
      .map(({ _key, ...rest }) => rest);
    await axios.put(`${API}/api/finance/journals/${t}/${b}/lines`, { lines: clean }, { headers: hdrs() });
  }, []);

  useEffect(() => {
    if (loading) return;
    const h = setTimeout(() => { persist().catch(() => {}); }, 800);
    return () => clearTimeout(h);
  }, [lines, persist, loading]);

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const u = { ...l, [field]: value };
      if (field === 'accountType') u.accountNo = '';       // reset lookup when type changes
      if (field === 'balAccountType') u.balAccountNo = '';
      return u;
    }));
  };
  const updateDim = (key, dimCode, value) => {
    setLines(prev => prev.map(l => l._key === key
      ? { ...l, dimensions: { ...(l.dimensions || {}), [dimCode]: value } } : l));
  };
  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => (prev.length === 1 ? [emptyLine()] : prev.filter(l => l._key !== key)));

  // Account lookup options per account type
  const accountOptions = (type) => {
    switch (type) {
      case 'G/L Account': return glAccountOptions(glAccounts);
      case 'Bank Account':
        return [<MenuItem key="_n" value=""><em>— Select —</em></MenuItem>,
          ...banks.map(b => <MenuItem key={b.accountNo} value={b.accountNo}>{b.accountNo} — {b.bankName}</MenuItem>)];
      case 'Vendor':
        return [<MenuItem key="_n" value=""><em>— Select —</em></MenuItem>,
          ...suppliers.map(s => <MenuItem key={s.supplierCode} value={s.supplierCode}>{s.supplierCode} — {s.supplierName}</MenuItem>)];
      case 'Customer':
        return [<MenuItem key="_n" value=""><em>— Select —</em></MenuItem>,
          ...learners.map(l => <MenuItem key={l.admNo} value={l.admNo}>{l.admNo} — {l.name || l.fullName || ''}</MenuItem>)];
      case 'Fixed Asset':
        return [<MenuItem key="_n" value=""><em>— Select —</em></MenuItem>,
          ...fixedAssets.map(a => <MenuItem key={a.faNo} value={a.faNo}>{a.faNo} — {a.faName}</MenuItem>)];
      case 'Employee':
        return [<MenuItem key="_n" value=""><em>— Select —</em></MenuItem>,
          ...employees.map(e => <MenuItem key={e.employeeNo} value={e.employeeNo}>{e.employeeNo} — {e.firstName} {e.lastName}</MenuItem>)];
      default: return [];
    }
  };

  // Totals. Lines WITH a balancing account are self-balancing, so they don't
  // count toward the out-of-balance figure (mirrors the server check).
  const num = (v) => parseFloat(v) || 0;
  const totalDr = lines.reduce((s, l) => s + num(l.debit), 0);
  const totalCr = lines.reduce((s, l) => s + num(l.credit), 0);
  const outOfBalance = lines.reduce((s, l) => l.balAccountNo ? s : s + (num(l.debit) - num(l.credit)), 0);
  const hasLines = lines.some(l => l.accountNo && (num(l.debit) > 0 || num(l.credit) > 0));
  const balanced = hasLines && Math.abs(outOfBalance) < 0.005;

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try { await persist(); setSuccess('Saved'); setTimeout(() => setSuccess(''), 1500); }
    catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handlePost = async () => {
    setPosting(true); setError(''); setSuccess('');
    try {
      await persist();  // ensure server has the latest before posting
      const { data } = await axios.post(`${API}/api/finance/journals/${templateCode}/${batchCode}/post`, {}, { headers: hdrs() });
      setSuccess(data.message || 'Posted');
      setLines([emptyLine()]);   // lines were deleted server-side
    } catch (err) {
      setError(err.response?.data?.error || 'Posting failed');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center">
              <ReceiptLong sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">General Journal</Typography>
            </Box>
            <IconButton onClick={() => navigate('/finance')} title="Finance Home"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
              <Home />
            </IconButton>
          </Box>

          {/* Template + Batch */}
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            <TextField select label="Journal Template" value={templateCode} size="small"
              onChange={e => setTemplateCode(e.target.value)} sx={{ minWidth: 220 }}>
              {templates.map(t => <MenuItem key={t.code} value={t.code}>{t.code} — {t.name}</MenuItem>)}
            </TextField>
            <TextField select label="Batch" value={batchCode} size="small"
              onChange={e => setBatchCode(e.target.value)} sx={{ minWidth: 200 }} disabled={!batches.length}>
              {batches.map(b => <MenuItem key={b.code} value={b.code}>{b.code} — {b.name}</MenuItem>)}
            </TextField>
          </Box>

          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {loading ? (
            <Box textAlign="center" py={4}><CircularProgress /></Box>
          ) : (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1250 + dimensions.length * 150 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 130 }}><strong>Posting Date</strong></TableCell>
                      <TableCell sx={{ minWidth: 120 }}><strong>Doc. Type</strong></TableCell>
                      <TableCell sx={{ minWidth: 110 }}><strong>Doc. No</strong></TableCell>
                      <TableCell sx={{ minWidth: 130 }}><strong>Account Type</strong></TableCell>
                      <TableCell sx={{ minWidth: 200 }}><strong>Account No</strong></TableCell>
                      <TableCell sx={{ minWidth: 160 }}><strong>Description</strong></TableCell>
                      {dimensions.map(d => (
                        <TableCell key={d.code} sx={{ minWidth: 140 }}><strong>{d.name}</strong></TableCell>
                      ))}
                      <TableCell align="right" sx={{ minWidth: 110 }}><strong>Debit</strong></TableCell>
                      <TableCell align="right" sx={{ minWidth: 110 }}><strong>Credit</strong></TableCell>
                      <TableCell sx={{ minWidth: 130 }}><strong>Bal. Acc. Type</strong></TableCell>
                      <TableCell sx={{ minWidth: 190 }}><strong>Bal. Account No</strong></TableCell>
                      <TableCell sx={{ width: 40 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map(l => (
                      <TableRow key={l._key}>
                        <TableCell>
                          <TextField type="date" value={l.postingDate} size="small" fullWidth
                            onChange={e => updateLine(l._key, 'postingDate', e.target.value)}
                            InputLabelProps={{ shrink: true }} />
                        </TableCell>
                        <TableCell>
                          <TextField select value={l.documentType} size="small" fullWidth
                            onChange={e => updateLine(l._key, 'documentType', e.target.value)}>
                            {DOCUMENT_TYPES.map(d => <MenuItem key={d || 'none'} value={d}>{d || '—'}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField value={l.documentNo} size="small" fullWidth placeholder="(auto)"
                            onChange={e => updateLine(l._key, 'documentNo', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField select value={l.accountType} size="small" fullWidth
                            onChange={e => updateLine(l._key, 'accountType', e.target.value)}>
                            {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField select value={l.accountNo} size="small" fullWidth
                            onChange={e => updateLine(l._key, 'accountNo', e.target.value)}>
                            {accountOptions(l.accountType)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField value={l.description} size="small" fullWidth
                            onChange={e => updateLine(l._key, 'description', e.target.value)} />
                        </TableCell>
                        {dimensions.map(d => (
                          <TableCell key={d.code}>
                            <TextField select value={(l.dimensions && l.dimensions[d.code]) || ''} size="small" fullWidth
                              onChange={e => updateDim(l._key, d.code, e.target.value)}>
                              <MenuItem value=""><em>—</em></MenuItem>
                              {(dimValues[d.code] || []).map(v => (
                                <MenuItem key={v.value} value={v.value}>{v.value} — {v.name}</MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                        ))}
                        <TableCell align="right">
                          <TextField value={l.debit} size="small" type="number"
                            inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                            onChange={e => updateLine(l._key, 'debit', e.target.value)}
                            disabled={num(l.credit) > 0} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField value={l.credit} size="small" type="number"
                            inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                            onChange={e => updateLine(l._key, 'credit', e.target.value)}
                            disabled={num(l.debit) > 0} />
                        </TableCell>
                        <TableCell>
                          <TextField select value={l.balAccountType} size="small" fullWidth
                            onChange={e => updateLine(l._key, 'balAccountType', e.target.value)}>
                            {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField select value={l.balAccountNo} size="small" fullWidth
                            onChange={e => updateLine(l._key, 'balAccountNo', e.target.value)}>
                            {accountOptions(l.balAccountType)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeLine(l._key)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={2}>
                <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small">Add Line</Button>
                <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">Total Debit: <strong>{sym()} {fmt(totalDr)}</strong></Typography>
                  <Typography variant="body2">Total Credit: <strong>{sym()} {fmt(totalCr)}</strong></Typography>
                  <Chip
                    label={balanced ? 'Balanced' : `Out of balance: ${sym()} ${fmt(outOfBalance)}`}
                    color={balanced ? 'success' : 'warning'} size="small"
                    variant={balanced ? 'filled' : 'outlined'} />
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                Lines with a balancing account are self-balancing and don't count toward the out-of-balance total.
              </Typography>

              <Box display="flex" gap={2} mt={3}>
                <Tooltip title={balanced ? '' : 'Journal must balance before posting'}>
                  <span>
                    <Button variant="contained" startIcon={<PostAdd />} onClick={handlePost}
                      disabled={!balanced || posting}>
                      {posting ? 'Posting…' : 'Post Journal'}
                    </Button>
                  </span>
                </Tooltip>
                <Button variant="outlined" startIcon={<Save />} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default GeneralJournal;
